/*
 * The Sales Reps filter.
 *
 * Replaces the old three-value Assignment dropdown (All / Mine / Unassigned).
 * Reps are now named and multi-selectable, and the filter lives in the URL and
 * in saved filters like every other multi-select on this page — the old one was
 * deliberately localStorage-only, and that difference is gone on purpose.
 */

/** Matches customers with no sales rep. Not a user id, so it cannot collide. */
export const UNASSIGNED_SALES_REP = 'UNASSIGNED';

/**
 * Legacy sentinel, read but never written. Saved filters created by the old
 * dropdown can still hold it, and the server resolves it to the caller.
 */
export const CURRENT_USER_SALES_REP = 'ME';

/** Marks that the one-time "start a rep on their own loads" seed has run. */
export const SALES_REP_SEED_KEY = 'org-orders:sales-reps-seeded';

export interface SalesRepOption {
  value: string;
  label: string;
}

/**
 * Options for the filter, Unassigned first.
 *
 * Pending org invites come back from `/organization/members` with
 * `user.firebaseUid: null`. They cannot be a sales rep, and two of them would
 * collide on a null key — the same filter the customer form's picker applies.
 */
export const buildSalesRepOptions = (members: any[] | null | undefined): SalesRepOption[] => {
  const people = (Array.isArray(members) ? members : [])
    .filter((member: any) => member?.user?.firebaseUid)
    .map((member: any) => ({
      value: member.user.firebaseUid as string,
      label: (member.user.name || member.user.email || 'Unnamed') as string,
    }));

  people.sort((a, b) => a.label.localeCompare(b.label));

  return [{ value: UNASSIGNED_SALES_REP, label: 'Unassigned' }, ...people];
};

/**
 * Whether to seed the filter with the current user, and what to seed it with.
 *
 * A sales rep opens the page on their own loads the first time, which is what
 * the old dropdown did by defaulting them to "Mine". It is a one-time guess:
 * once seeded, an empty selection means the user chose "All" and must stay
 * that way. Returns null when there is nothing to do.
 */
export const resolveSalesRepSeed = (
  me: { firebaseUid?: string | null; salesRepAddresses?: unknown[] } | null | undefined,
  currentValue: string[],
  alreadySeeded: boolean
): string[] | null => {
  if (!me || alreadySeeded) {
    return null;
  }

  // A URL or saved filter already said what to show; never override it.
  if (currentValue.length > 0) {
    return null;
  }

  // Reps are assigned per delivery location; a rep gets seeded to their own loads.
  const isRep = Boolean(me.salesRepAddresses?.length);
  if (!isRep || !me.firebaseUid) {
    return null;
  }

  return [me.firebaseUid];
};
